import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, Database, Activity, Link2 } from 'lucide-react';
import { TimelineEventService } from '@/services/video-call/timelineEventService';
import { supabase } from '@/supabase/supabase';

interface HealthStatus {
  status: 'healthy' | 'unhealthy';
  supabaseConnection: boolean;
  callEventsTable: boolean;
  callLogsTable: boolean;
  timestamp: string;
  error?: string;
}

interface TestEventResult {
  success: boolean;
  eventId?: string;
  error?: string;
  eventsAdded?: number;
  totalEvents?: number;
}

export const HealthCheck: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [testEventResult, setTestEventResult] = useState<TestEventResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isBasicChecking, setIsBasicChecking] = useState(false);

  const runHealthCheck = async () => {
    setIsChecking(true);
    setHealthStatus(null);
    console.log('🔍 Starting health check from UI...');
    
    try {
      // Basic Supabase connection test
      const { data, error } = await supabase.from('call_logs').select('id').limit(1);
      
      const result: HealthStatus = {
        status: error ? 'unhealthy' : 'healthy',
        supabaseConnection: !error,
        callEventsTable: true, // We're using timeline_events now
        callLogsTable: !error,
        timestamp: new Date().toISOString(),
        error: error?.message
      };
      
      setHealthStatus(result);
      console.log('✅ Health check completed:', result);
    } catch (error) {
      console.error('❌ Health check failed:', error);
      setHealthStatus({
        status: 'unhealthy',
        supabaseConnection: false,
        callEventsTable: false,
        callLogsTable: false,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsChecking(false);
    }
  };

  const runBasicHealthCheck = async () => {
    setIsBasicChecking(true);
    setHealthStatus(null);
    console.log('🔍 Starting basic health check from UI...');
    try {
      // Basic Supabase connection test
      const { data, error } = await supabase.from('call_logs').select('id').limit(1);
      
      const result: HealthStatus = {
        status: error ? 'unhealthy' : 'healthy',
        supabaseConnection: !error,
        callEventsTable: true, // We're using timeline_events now
        callLogsTable: !error,
        timestamp: new Date().toISOString(),
        error: error?.message
      };
      
      setHealthStatus(result);
      console.log('✅ Basic health check completed:', result);
    } catch (error) {
      console.error('❌ Basic health check failed:', error);
      setHealthStatus({
        status: 'unhealthy',
        supabaseConnection: false,
        callEventsTable: false,
        callLogsTable: false,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsBasicChecking(false);
    }
  };

  const testEventStorage = async () => {
    setIsTesting(true);
    setTestEventResult(null);
    console.log('🔍 Testing timeline_events update in call_logs from UI...');

    // TODO: Replace with dynamic test data or environment variables
    const callId = import.meta.env.VITE_TEST_CALL_ID || 'test-call-id';
    const organizationId = import.meta.env.VITE_TEST_ORGANIZATION_ID || 'test-org-id';
    const newTimelineEvent = {
      type: 'ui_test',
      message: 'HealthCheck UI test event',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    try {
      // Fetch current timeline_events
      const { data: callLog, error: fetchError } = await supabase
        .from('call_logs')
        .select('timeline_events')
        .eq('id', callId)
        .eq('organization_id', organizationId)
        .single();

      if (fetchError) {
        setTestEventResult({ success: false, error: 'Fetch failed: ' + fetchError.message });
        console.error('❌ Fetch call_log failed:', fetchError);
        setIsTesting(false);
        return;
      }

      let timelineEvents = Array.isArray(callLog?.timeline_events) ? callLog.timeline_events : [];
      timelineEvents = [...timelineEvents, newTimelineEvent];

      // Update timeline_events
      const { error: updateError } = await supabase
        .from('call_logs')
        .update({ timeline_events: timelineEvents })
        .eq('id', callId)
        .eq('organization_id', organizationId);

      if (updateError) {
        setTestEventResult({ success: false, error: 'Update failed: ' + updateError.message });
        console.error('❌ Update timeline_events failed:', updateError);
      } else {
        setTestEventResult({ success: true });
        console.log('✅ timeline_events updated successfully');
      }
    } catch (error) {
      console.error('❌ timeline_events update exception:', error);
      setTestEventResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsTesting(false);
    }
  };

  const testTimelineEventService = async () => {
    setIsTesting(true);
    setTestEventResult(null);
    console.log('🔍 Testing TimelineEventService from UI...');

    const callId = import.meta.env.VITE_TEST_CALL_ID || 'test-call-id'; // Use environment variable or fallback
    
    try {
      // Create a test timeline event using the new service
      const timelineEvent = TimelineEventService.createTabChangeEvent(
        'hidden',
        undefined,
        { 
          source: 'HealthCheck UI test',
          timestamp: new Date().toISOString()
        }
      );

      // Add the event using the new service
      await TimelineEventService.addTimelineEvent(callId, timelineEvent);
      
      setTestEventResult({ success: true, eventId: callId });
      console.log('✅ TimelineEventService test completed successfully');
    } catch (error: any) {
      console.error('❌ TimelineEventService test failed:', error);
      setTestEventResult({ 
        success: false, 
        error: error?.message || 'Unknown error' 
      });
    } finally {
      setIsTesting(false);
    }
  };

  const testApiTimelineEvent = async () => {
    setIsTesting(true);
    setTestEventResult(null);
    console.log('🔍 Testing POST /api/call/{call_id}/timeline-events from UI...');

    const callId = import.meta.env.VITE_TEST_CALL_ID || 'test-call-id'; // Use environment variable or fallback
    const apiUrl = `${import.meta.env.VITE_CALL_SERVICE_URL || 'http://localhost:8000'}/api/call/${callId}/timeline-events`;
    const newTimelineEvent = {
      type: 'ui_test_api',
      action: 'test',
      timestamp: new Date().toISOString(),
      data: {
        message: 'HealthCheck API test event',
        userAgent: navigator.userAgent,
        url: window.location.href
      }
    };

    try {
      // Send POST request to API
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_TEST_API_KEY || 'test-api-key'
        },
        body: JSON.stringify({ events: [newTimelineEvent] }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        setTestEventResult({ success: false, error: `API POST failed: ${response.status} - ${errorText}` });
        setIsTesting(false);
        return;
      }

      const data = await response.json();
      setTestEventResult({ 
        success: true, 
        eventId: data?.call_id || callId,
        eventsAdded: data?.events_added,
        totalEvents: data?.total_events
      });
      setIsTesting(false);
    } catch (error: any) {
      setTestEventResult({ success: false, error: error?.message || 'Unknown error' });
      setIsTesting(false);
    }
  };

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    );
  };

  const getStatusBadge = (status: 'healthy' | 'unhealthy') => {
    return (
      <Badge variant={status === 'healthy' ? 'default' : 'destructive'}>
        {status === 'healthy' ? 'Healthy' : 'Unhealthy'}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Supabase Health Check
          </CardTitle>
          <CardDescription>
            Test the connection to Supabase and the call_events/call_logs tables
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-2">
            <Button 
              onClick={runHealthCheck} 
              disabled={isChecking}
              className="w-full md:w-auto"
            >
              {isChecking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Full Health Check...
                </>
              ) : (
                <>
                  <Activity className="mr-2 h-4 w-4" />
                  Full Health Check
                </>
              )}
            </Button>
            <Button 
              onClick={runBasicHealthCheck} 
              disabled={isBasicChecking}
              className="w-full md:w-auto"
              variant="outline"
            >
              {isBasicChecking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Basic Connection Only...
                </>
              ) : (
                <>
                  <Link2 className="mr-2 h-4 w-4" />
                  Basic Supabase Connection Only
                </>
              )}
            </Button>
          </div>

          {healthStatus && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium">Overall Status:</span>
                {getStatusBadge(healthStatus.status)}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span>Supabase Connection:</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(healthStatus.supabaseConnection)}
                    <span>{healthStatus.supabaseConnection ? 'Connected' : 'Failed'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span>call_events Table:</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(healthStatus.callEventsTable)}
                    <span>{healthStatus.callEventsTable ? 'Accessible' : 'Failed'}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span>call_logs Table:</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(healthStatus.callLogsTable)}
                    <span>{healthStatus.callLogsTable ? 'Accessible' : 'Failed'}</span>
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                <div>Timestamp: {new Date(healthStatus.timestamp).toLocaleString()}</div>
                {healthStatus.error && (
                  <div className="text-red-600 mt-2">
                    <strong>Error:</strong> {healthStatus.error}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Event Storage Test
          </CardTitle>
          <CardDescription>
            Test storing a call event directly to verify the storage pipeline
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={testEventStorage} 
            disabled={isTesting}
            className="w-full"
            variant="outline"
          >
            {isTesting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing Event Storage...
              </>
            ) : (
              'Test Event Storage'
            )}
          </Button>

          {testEventResult && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium">Storage Test:</span>
                <div className="flex items-center gap-2">
                  {getStatusIcon(testEventResult.success)}
                  <span>{testEventResult.success ? 'Success' : 'Failed'}</span>
                </div>
              </div>
              
              {testEventResult.error && (
                <div className="text-sm text-red-600">
                  <strong>Error:</strong> {testEventResult.error}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Timeline Event Service Test</CardTitle>
          <CardDescription>
            Test the new TimelineEventService that replaces CallEventService for storing timeline events
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-2">
            <Button onClick={testTimelineEventService} disabled={isTesting} className="w-full md:w-auto">
              {isTesting ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
              Test TimelineEventService
            </Button>
            <Button onClick={testApiTimelineEvent} disabled={isTesting} className="w-full md:w-auto" variant="outline">
              {isTesting ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
              Test API Direct
            </Button>
          </div>
          {testEventResult && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium">Timeline Event Test:</span>
                <div className="flex items-center gap-2">
                  {getStatusIcon(testEventResult.success)}
                  <span>{testEventResult.success ? 'Success' : 'Failed'}</span>
                </div>
              </div>
              
              {testEventResult.success && (
                <div className="text-sm text-green-600">
                  Event added successfully to call: {testEventResult.eventId}
                </div>
              )}
              
              {testEventResult.error && (
                <div className="text-sm text-red-600">
                  <strong>Error:</strong> {testEventResult.error}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <div>1. <strong>Full Health Check:</strong> Tests Supabase connectivity and table access</div>
          <div>2. <strong>Basic Connection Only:</strong> Only tests Supabase client connection</div>
          <div>3. <strong>Event Storage Test:</strong> Tests direct database storage (legacy method)</div>
          <div>4. <strong>TimelineEventService:</strong> Tests the new API-based timeline event storage (recommended)</div>
          <div>5. <strong>API Direct:</strong> Tests the timeline events API endpoint directly</div>
          <div>6. Check browser console for detailed logs with 🔵/✅/❌ prefixes</div>
          <div>7. Make sure your <code>.env</code> has correct VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY</div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HealthCheck; 